<template>
	<div
		ref="rootEl"
		class="base-select"
		:class="rootNodeClasses"
		role="listbox"
		aria-haspopup="true"
		:aria-expanded="`${open}`"
		@click="toggleDropdown"
	>
		<div class="base-select__inner">
			<div class="base-select__content">
				<p v-if="hasValue" class="base-select__selected-option">
					{{ selectedLabel }}
				</p>

				<p v-else class="base-select__placeholder">
					{{ placeholder }}
				</p>
			</div>

			<span class="base-select__indicator" />
		</div>

		<Teleport to="body">
			<div
				class="base-select__dropdown base-select-dropdown"
				:style="dropdownStyle"
				:class="{
					'base-select-dropdown--is-open': open,
					'base-select-dropdown--bottom': bottomMode,
					'base-select-dropdown--is-small': small,
				}"
			>
				<div class="base-select__dropdown-background" />

				<ul ref="listEl" class="base-select__list" tabindex="-1" role="listbox" @click.stop>
					<li v-if="hasListHeader" class="base-select__list-item base-select__list-header">
						<slot name="list-header" />
					</li>

					<li v-if="noOptions" class="base-select__list-item base-select__list-empty">
						<slot name="list-empty">
							<p class="base-select__option-content">
								{{ listEmpty }}
							</p>
						</slot>
					</li>

					<template v-for="(option, index) in options">
						<li
							v-if="displayOption(index)"
							:key="index"
							class="base-select__list-item base-select__option"
							:class="getOptionClasses(index)"
							role="option"
							:aria-selected="isSelected(index)"
							:aria-disabled="isDisabled(index)"
							@click="onOptionClicked(index)"
						>
							<p class="base-select__option-content">
								{{ option[labelKey] }}
							</p>
						</li>
					</template>

					<li v-if="hasListFooter" class="base-select__list-item base-select__list-footer">
						<slot name="list-footer" />
					</li>
				</ul>
			</div>
		</Teleport>
	</div>
</template>

<script lang="ts">
import { useElementBounding } from '@vueuse/core';
import { Dictionary, isNil } from 'lodash';
import { computed, defineComponent, PropType, reactive, ref, StyleValue, toRefs, watch } from 'vue';

import { useHasSlot } from '@/composables';
import { flagsToCssClasses } from '@/tools/helpers';
import { AnyObject, VueClassBinding } from '@/tools/types';

import { OptionValue } from './types';

/**
 * TODO:
 * - removing selected item
 * - custom structure through slot's and components separation
 * - accessibility
 * - searching
 * - sorting
 * - handle native name in a proper way
 */

export default defineComponent({
	props: {
		/**
		 * Native name
		 */
		name: {
			type: String,
			default: '',
		},
		/**
		 * v-model prop
		 */
		modelValue: {
			type: [String, Number, Boolean] as PropType<OptionValue | null>,
			default: null,
		},
		/**
		 * Name of a field that contains label in a single option
		 */
		labelKey: {
			type: String,
			default: 'label',
		},
		/**
		 * Name of a field that contains value in a single option
		 */
		valueKey: {
			type: String,
			default: 'value',
		},
		/**
		 * Select options
		 */
		options: {
			type: Array as PropType<AnyObject[]>,
			default: () => [],
		},
		/**
		 * Placeholder when no value is selected
		 */
		placeholder: {
			type: String,
			default: 'Please select a value',
		},
		/**
		 * Text to display if list is empty and slot is not provided
		 */
		listEmpty: {
			type: String,
			default: 'No options to choose from',
		},
		/**
		 * Hide selected option in list. Filtering passed options is not advisable
		 * as it results in unexpected behaviour. This could be changed in a future.
		 */
		hideSelectedOption: {
			type: Boolean,
		},

		/**
		 * Styling option to make the component smaller
		 */
		small: {
			type: Boolean,
		},

		/**
		 * Should select default first if only one option available.
		 */
		defaultsToFirstOnOneOption: {
			type: Boolean,
			default: false,
		},
	},

	emits: ['toggle', 'update:modelValue'],

	setup(props, { slots, emit }) {
		const rootEl = ref<HTMLElement>();
		const listEl = ref<HTMLElement>();
		const data = reactive({
			open: false,
			selectedOptionIndex: -1,
		});

		let rootBbox = useElementBounding(rootEl);
		let listBbox = useElementBounding(listEl);

		const selectedOption = computed(() => {
			if (data.selectedOptionIndex === -1) {
				return null;
			}

			return props.options[data.selectedOptionIndex] ?? null;
		});
		const selectedValue = computed(() => {
			return selectedOption.value?.[props.valueKey] ?? null;
		});
		const selectedLabel = computed(() => {
			return selectedOption.value?.[props.labelKey] ?? selectedValue.value ?? '';
		});

		watch(
			() => props.modelValue,
			() => {
				data.selectedOptionIndex = props.options.findIndex(
					option => option[props.valueKey] === props.modelValue,
				);
				if (
					props.defaultsToFirstOnOneOption &&
					props.options.length === 1 &&
					isNil(props.modelValue)
				) {
					data.selectedOptionIndex = 0;
					emit('update:modelValue', selectedValue.value);
				}
			},
			{ immediate: true },
		);
		watch(
			() => data.selectedOptionIndex,
			() => {
				if (selectedValue.value !== props.modelValue) {
					onOptionSelected();
				}
			},
		);
		watch(
			() => data.open,
			() => {
				emit('toggle', data.open);

				// fix for wrong top value of rootBox until page refresh
				// TODO: needs more research and better solution
				rootBbox = useElementBounding(rootEl);
				listBbox = useElementBounding(listEl);
			},
		);

		function onOptionSelected(): void {
			emit('update:modelValue', selectedValue.value);
			rootEl.value?.dispatchEvent(new Event('change', { bubbles: true, cancelable: false }));
		}

		const bottomMode = computed(() => {
			if (
				window.innerHeight - (rootBbox.top.value + rootBbox.height.value) <
				listBbox.height.value
			) {
				return true;
			}

			return false;
		});

		const dropdownStyle = computed<StyleValue>(() => {
			if (!data.open) return {};

			if (bottomMode.value) {
				return {
					bottom: `${window.innerHeight - rootBbox.top.value}px`,
					top: 'auto',
					left: `${rootBbox.left.value}px`,
					width: `${rootBbox.width.value}px`,
				};
			}

			return {
				top: `${rootBbox.top.value + rootBbox.height.value}px`,
				left: `${rootBbox.left.value}px`,
				width: `${rootBbox.width.value}px`,
			};
		});

		return {
			...toRefs(data),

			dropdownStyle,
			bottomMode,

			selectedOption,
			selectedLabel,
			selectedValue,
			listEl,
			rootEl,

			hasListHeader: useHasSlot('list-header', slots),
			hasListFooter: useHasSlot('list-footer', slots),
		};
	},

	computed: {
		hasValue(): boolean {
			return !isNil(this.selectedValue) && this.selectedValue !== '';
		},

		isSelected(): (index: number) => true | null {
			return index => (index === this.selectedOptionIndex ? true : null);
		},

		isDisabled(): (index: number) => true | null {
			return index => (this.options[index]?.disabled ? true : null);
		},

		/**
		 * Returns index from a rendered perspective. Differes only if `hideSelectedOption` is true.
		 */
		getRelativeIndex(): (index: number) => number {
			return index => {
				if (this.hideSelectedOption && this.selectedOption) {
					return index > this.selectedOptionIndex ? index - 1 : index;
				}

				return index;
			};
		},

		getOptionClasses(): (realIndex: number) => VueClassBinding {
			const baseClass = 'base-select__option';

			return realIndex => {
				let optionsAmount = this.options.length;
				let relativeIndex = this.getRelativeIndex(realIndex);

				if (this.hideSelectedOption && this.selectedOption) {
					optionsAmount--;
				}

				return {
					[`${baseClass}--is-selected`]: !!this.isSelected(realIndex),
					[`${baseClass}--is-first`]: relativeIndex === 0,
					[`${baseClass}--is-last`]: relativeIndex === optionsAmount - 1,
					[`${baseClass}--is-disabled`]: !!this.isDisabled(realIndex),
				};
			};
		},

		displayOption(): (index: number) => boolean {
			return index => (this.hideSelectedOption ? index !== this.selectedOptionIndex : true);
		},

		noOptions(): boolean {
			const optionsAmount = this.options.length;

			if (this.hideSelectedOption) {
				return !(this.selectedOption ? optionsAmount - 1 : optionsAmount);
			}

			return !optionsAmount;
		},

		rootNodeClasses(): Dictionary<boolean> {
			return flagsToCssClasses('base-select', this, ['open', 'small']);
		},
	},

	mounted() {
		document.addEventListener('click', this.onDocumentClicked);
	},

	beforeUnmount() {
		document.removeEventListener('click', this.onDocumentClicked);
	},

	methods: {
		onOptionSelected(): void {
			this.$emit('update:modelValue', this.selectedValue);
			this.$el.dispatchEvent(new Event('change', { bubbles: true, cancelable: false }));
		},

		onOptionClicked(index: number): void {
			if (index === this.selectedOptionIndex) {
				return;
			}

			this.selectedOptionIndex = index;
			this.closeDropdown();
		},

		closeDropdown(): void {
			this.open = false;
		},

		openDropdown(): void {
			this.open = true;
		},

		toggleDropdown(): void {
			this.open = !this.open;
		},

		onDocumentClicked({ target }: MouseEvent): void {
			if (!this.open || !target) {
				return;
			}

			if (!this.$el.contains(target as Node)) {
				this.closeDropdown();
			}
		},
	},
});
</script>

<style lang="scss">
.base-select {
	$self: &;
	$select-height: 8.8rem;
	$select-height-small: 4.8rem;
	$select-border-radius: 0.8rem;

	@include heading-3;

	width: 100%;
	height: $select-height;
	position: relative;
	z-index: 1000;
	cursor: pointer;

	&__inner {
		@include dropshadow-input-small;
		display: flex;
		width: 100%;
		height: 100%;
		transition: box-shadow 250ms $easeOutQuad;
		border: none;
		border-radius: $select-border-radius;
		background-color: rgba($color-white, 0.9);
	}

	&__content {
		display: flex;
		align-items: center;
		width: 100%;
		min-width: 0;
	}

	&__placeholder {
		color: $color-gray;
	}

	&__selected-option {
		color: $color-black;
	}

	&__placeholder,
	&__selected-option {
		padding-left: 1.6rem;
		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;
	}

	&__indicator {
		min-width: 4rem;
		margin: 0 1.6rem;
		position: relative;

		&::after {
			content: '';
			display: block;
			width: 1.3rem;
			height: 1.3rem;
			position: absolute;
			top: 50%;
			left: 50%;
			transition: transform 250ms $easeOutQuad;
			transform: translate(-75%, -75%) rotate(45deg);
			transform-origin: 75% 75%;
			border: 1px solid $color-dark;
			border-width: 0 1px 1px 0;
		}
	}

	&__dropdown {
		width: 100%;
		position: absolute;
		top: 100%;
		top: calc(100% - #{$select-border-radius});
		z-index: 1;
		word-break: break-all;
		visibility: hidden;
		list-style: none;
		transition:
			visibility 250ms $easeOutQuad,
			opacity 250ms $easeOutQuad;
		opacity: 0;
		border-bottom-right-radius: $select-border-radius;
		border-bottom-left-radius: $select-border-radius;
		background: rgba($color-white, 0.9);
		will-change: visibility;

		&::before {
			@include dropshadow-input-small;
			content: '';
			display: block;
			position: absolute;
			top: -$select-height + $select-border-radius;
			right: 0;
			bottom: 0;
			left: 0;
			border-radius: $select-border-radius;
		}
	}

	&__list {
		max-height: min(31rem, 40vh);
		position: relative;
		overflow: auto;
		list-style: none;
		cursor: default;
		outline: none;
	}

	&__list-item {
		display: flex;
		align-items: center;
		height: $select-height;
		padding: 0 1.6rem;
		color: $color-gray;

		.base-select-dropdown--is-small & {
			height: $select-height-small;
		}
	}

	&__option {
		cursor: pointer;
		transition: background-color 250ms $easeOutQuad;
		background-color: rgba($color-light, 0.36);

		&--is-first {
			border-top-left-radius: $select-border-radius;
			border-top-right-radius: $select-border-radius;
		}

		&--is-last {
			border-bottom-right-radius: $select-border-radius;
			border-bottom-left-radius: $select-border-radius;
		}

		&--is-disabled {
			pointer-events: none;
		}

		&--is-disabled,
		&--is-selected {
			color: rgba($color-gray, 0.3);
			cursor: default;
		}

		&:not(&--is-selected) {
			&:hover,
			&:focus {
				background-color: rgba($color-white, 0.9);
			}
		}
	}

	&__list-header {
		border-top-left-radius: $select-border-radius;
		border-top-right-radius: $select-border-radius;
	}

	&__list-footer {
		border-bottom-right-radius: $select-border-radius;
		border-bottom-left-radius: $select-border-radius;
	}

	&__list-header,
	&__list-footer {
		background-color: rgba($color-white, 0.9);
	}

	&__option-content {
		width: 100%;
		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;
	}

	&__dropdown-background {
		display: none;
		position: absolute;
		z-index: -1;
	}

	&--is-open {
		#{$self} {
			&__dropdown {
				visibility: visible;
				opacity: 1;
			}

			&__inner {
				box-shadow: none;
			}

			&__content {
				opacity: 0.6;
			}

			&__indicator::after {
				transform: translate(-75%, -75%) rotate(45deg) rotate3d(-1, 1, 0, -180deg);
			}
		}
	}

	&--is-small {
		@include small-text;
		height: $select-height-small;
	}

	&-dropdown {
		@include heading-3;
		display: none;
		cursor: pointer;

		&--is-open {
			display: block;
			margin-top: -#{$select-border-radius};
			position: fixed;
			z-index: 999999;
			visibility: visible;
			opacity: 1;
		}

		&--bottom {
			border-top-left-radius: $select-border-radius;
			border-top-right-radius: $select-border-radius;
			border-bottom-right-radius: 0;
			border-bottom-left-radius: 0;

			&::before {
				top: 0;
				bottom: calc(-8rem - #{$select-border-radius});
			}
		}

		&--is-small {
			@include small-text;
			background: $color-white;

			&::before {
				top: -4rem;
			}
		}
	}
}
</style>
